/**
 * Trackball port of Nanome.Infrastructure/Controls/TrackballControl.cs. It moves the scene root, never the camera.
 *
 * Mouse (TrackballControl.cs:116-191, 443-465):
 *  - left drag: Rotate(delta): axis = (delta.y, -delta.x, 0) (Unity, y up), angle = |delta| degrees, about the pivot.
 *  - right drag: Pan: position += (dx, dy) * 0.0005 m/px.
 *  - wheel: Zoom: scale *= 1.01^(scroll * zoomSpeed), zoomSpeed 3 (SceneManager.prefab:311), clamped to [0.1, 10] x
 *    the initial scale (0.02), about the zoom pivot. See WHEEL_DELTA_TO_SCROLL for the DOM calibration.
 *  - pivot (:404-423) = selection centre ?? hovered atom ?? centre of the entry under the cursor ?? scene origin.
 *  - zoom pivot locks for ZoomPivotHoldSeconds = 0.5 s while scrolling continues (:390-402).
 * Touch (:470-560): 1 finger rotates; 2 fingers pinch (delta * 0.1) or pan, locked after 10 px with 0.85 decay.
 *
 * Frame conversion (see frame.ts): Unity is left-handed, GL right-handed (z flipped) and DOM y points down.
 * Unity axis (dy_u, -dx, 0) with dy_u = -dy_dom is (-dy_dom, -dx, 0); conjugating a rotation by diag(1,1,-1) maps
 * axis (ax, ay, az) to (-ax, -ay, az), so the GL axis is (dy_dom, dx_dom, 0) with the same angle. Pan: GL y = -dy_dom.
 */
import { Quaternion, Vector3 } from 'three';
import { ANGSTROM_TO_METRE, TRACKBALL } from '@nanome-lite/look';
import type { SceneTransform } from './frame';

export interface RotateDelta { axis: [number, number, number]; angleDeg: number }

/** GL rotation axis and angle for a pointer drag of (dx, dy) DOM pixels (y down). */
export function rotationFromDrag(dxPx: number, dyPx: number): RotateDelta | null {
  const mag = Math.hypot(dxPx, dyPx);
  if (mag === 0) return null;
  return { axis: [dyPx / mag, dxPx / mag, 0], angleDeg: mag * TRACKBALL.rotateDegPerPx };
}

const _q = new Quaternion();
const _axis = new Vector3();
const _tmp = new Vector3();

/** transform.RotateAround(pivot, axis, angle) on the scene root. */
export function rotateAround(t: SceneTransform, pivot: Vector3, axis: [number, number, number], angleDeg: number): void {
  _axis.set(axis[0], axis[1], axis[2]).normalize();
  _q.setFromAxisAngle(_axis, (angleDeg * Math.PI) / 180);
  _tmp.copy(t.position).sub(pivot).applyQuaternion(_q).add(pivot);
  t.position.copy(_tmp);
  t.quaternion.premultiply(_q);
}

export const ZOOM_SCALE_MIN = ANGSTROM_TO_METRE * TRACKBALL.zoomRange[0];
export const ZOOM_SCALE_MAX = ANGSTROM_TO_METRE * TRACKBALL.zoomRange[1];

/** New scale for a scroll delta (TrackballControl.cs:450-459). */
export function zoomScale(oldScale: number, scrollDelta: number): number {
  return Math.min(ZOOM_SCALE_MAX, Math.max(ZOOM_SCALE_MIN, oldScale * Math.pow(TRACKBALL.zoomBase, scrollDelta)));
}

/** Zoom about a pivot: position = pivot + (position - pivot) * ratio. */
export function zoomAbout(t: SceneTransform, pivot: Vector3, scrollDelta: number): void {
  const newScale = zoomScale(t.scale, scrollDelta);
  const ratio = newScale / t.scale;
  _tmp.copy(t.position).sub(pivot).multiplyScalar(ratio).add(pivot);
  t.position.copy(_tmp);
  t.scale = newScale;
}

/** Right-drag pan in DOM pixels (y down). */
export function pan(t: SceneTransform, dxPx: number, dyPx: number): void {
  t.position.x += dxPx * TRACKBALL.panMetresPerPx;
  t.position.y -= dyPx * TRACKBALL.panMetresPerPx;
}

/**
 * TrackballControl.zoomSpeed as serialised on the live instance (SceneManager.prefab:306-313: rotateSpeed 1,
 * panSpeed 0.0005, zoomSpeed 3, minZoom 0.1, maxZoom 10). Zoom(scroll.y * zoomSpeed) and the touch pinch
 * (pinchDelta * zoomSpeed * 0.1, TrackballControl.cs:549) both use it. Loading.unity:224 holds a stale 0.1 from an
 * older version of the script (its other fields no longer exist) and is not used by the viewer.
 */
export const ZOOM_SPEED = 3;

/**
 * DOM wheel delta (px) -> Unity scroll units, before ZOOM_SPEED. Not in the source; derived as follows.
 *
 * Nanome reads Mouse.scroll through Input System 1.19 on Unity 6000.3 with the default
 * InputSettings.scrollDeltaBehavior = UniformAcrossAllPlatforms (InputSystem.inputsettings.asset leaves it unset),
 * which normalises one wheel notch to +-1. One notch therefore zooms by 1.01^3 = 3.03 % in Nanome.
 *
 * Browsers report pixels (deltaMode 0) with different sizes per notch:
 *   - Chrome / Edge on Windows and Linux: 100 px per notch (120 wheel units scaled to 100 px);
 *   - Chrome on macOS with a mouse: about 40 px per slow notch (kScrollbarPixelsPerCocoaTick), larger with macOS
 *     wheel acceleration; Safari reports a few px per notch, also accelerated;
 *   - Firefox: deltaMode 1 (3 lines) per notch, or 48-100 px depending on the platform;
 *   - macOS trackpad: a stream of small pixel deltas (1-20 px each) with momentum, a few hundred px per flick.
 * 1/100 per px makes a 100 px notch exactly Nanome's 3.03 %; a 40 px macOS Chrome notch gives 1.2 %, and a 300 px
 * trackpad flick about 9 %. Unity's WebGL backend rescales DOM deltas per platform too (its scroll steps are known to
 * differ between mouse and trackpad), so an exact match on every device is not possible from a single constant; hosts
 * can override it with EngineOptions.wheelDeltaToScroll.
 */
export const WHEEL_DELTA_TO_SCROLL = 0.01;
/** Per-event clamp in Unity scroll units (before ZOOM_SPEED): 20 notches. */
export const WHEEL_SCROLL_CLAMP = 20;

/** Unity scroll units (one notch = 1) for a DOM wheel event; scroll-down (positive deltaY) zooms out. */
export function wheelToScroll(deltaY: number, deltaMode: number, deltaToScroll: number = WHEEL_DELTA_TO_SCROLL): number {
  // deltaMode 1 = lines, 2 = pages (Firefox); normalise to pixels first, 100/3 px per line so that Firefox's
  // 3-line notch counts as one 100 px notch.
  const px = deltaMode === 1 ? deltaY * (100 / 3) : deltaMode === 2 ? deltaY * 400 : deltaY;
  const s = -px * deltaToScroll;
  return Math.max(-WHEEL_SCROLL_CLAMP, Math.min(WHEEL_SCROLL_CLAMP, s));
}

/** Scale factor one wheel event applies (TrackballControl.Zoom with zoomSpeed), before the [0.1, 10] clamp. */
export function wheelZoomFactor(deltaY: number, deltaMode: number, deltaToScroll: number = WHEEL_DELTA_TO_SCROLL): number {
  return Math.pow(TRACKBALL.zoomBase, wheelToScroll(deltaY, deltaMode, deltaToScroll) * ZOOM_SPEED);
}

export type TwoFingerGesture = 'none' | 'zoom' | 'pan';

/** Two-finger gesture lock state machine (TrackballControl.cs:514-545). Pure; returns the gesture to apply. */
export function updateTwoFingerGesture(
  state: { gesture: TwoFingerGesture; pinchAcc: number; panAcc: number },
  pinchDelta: number,
  panDeltaMag: number,
): TwoFingerGesture {
  const { touchDecay, touchLockPx } = TRACKBALL;
  state.pinchAcc = state.pinchAcc * touchDecay + Math.abs(pinchDelta);
  state.panAcc = state.panAcc * touchDecay + panDeltaMag;
  switch (state.gesture) {
    case 'none':
      if (state.panAcc >= touchLockPx || state.pinchAcc >= touchLockPx) state.gesture = state.panAcc >= state.pinchAcc ? 'pan' : 'zoom';
      break;
    case 'zoom':
      if (state.panAcc >= touchLockPx && state.panAcc > state.pinchAcc) state.gesture = 'pan';
      break;
    case 'pan':
      if (state.pinchAcc >= touchLockPx && state.pinchAcc > state.panAcc) state.gesture = 'zoom';
      break;
  }
  return state.gesture;
}

export interface TrackballHost {
  transform: SceneTransform;
  /** pivot resolution in GL world metres; null means the scene origin (transform.position) */
  pivotAt(clientX: number, clientY: number): Vector3 | null;
  /** called after every change so the host can redraw */
  changed(): void;
  /** hover suppression while a button is held (MouseStructureInteractor.cs:146-157) */
  setDragging(dragging: boolean): void;
  /** click (press/release moved < 4 px, MouseStructureInteractor.cs:29) */
  click(clientX: number, clientY: number, ev: { shiftKey: boolean; ctrlKey: boolean }): void;
  /** pointer moved without buttons (hover) */
  hover(clientX: number, clientY: number): void;
  hoverEnd(): void;
  /** DOM px -> Unity scroll units; default WHEEL_DELTA_TO_SCROLL */
  wheelDeltaToScroll?: number;
}

const CLICK_THRESHOLD_PX = 4; // MouseStructureInteractor.cs:29

/** DOM event binding for the trackball. */
export class TrackballController {
  private lastX = 0;
  private lastY = 0;
  private button = -1;
  private downX = 0;
  private downY = 0;
  private moved = false;
  private rotationPivot: Vector3 | null = null;
  private zoomPivot: Vector3 | null = null;
  private lastZoomTime = -Infinity;
  private readonly touches = new Map<number, { x: number; y: number }>();
  private twoFinger = { gesture: 'none' as TwoFingerGesture, pinchAcc: 0, panAcc: 0 };
  private lastPinchDistance = 0;
  private lastPinchMid = { x: 0, y: 0 };
  private readonly abort = new AbortController();

  constructor(private readonly el: HTMLElement, private readonly host: TrackballHost) {
    const o = { signal: this.abort.signal };
    el.addEventListener('pointerdown', this.onPointerDown, o);
    el.addEventListener('pointermove', this.onPointerMove, o);
    el.addEventListener('pointerup', this.onPointerUp, o);
    el.addEventListener('pointercancel', this.onPointerUp, o);
    el.addEventListener('pointerleave', this.onPointerLeave, o);
    el.addEventListener('wheel', this.onWheel, { passive: false, signal: this.abort.signal });
    el.addEventListener('contextmenu', (e) => e.preventDefault(), o);
    el.style.touchAction = 'none';
  }

  dispose(): void { this.abort.abort(); }

  private pivot(x: number, y: number): Vector3 { return this.host.pivotAt(x, y) ?? this.host.transform.position.clone(); }

  private onPointerDown = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') { this.onTouchChange(e, 'down'); return; }
    this.el.setPointerCapture?.(e.pointerId);
    this.button = e.button;
    this.lastX = this.downX = e.clientX; this.lastY = this.downY = e.clientY;
    this.moved = false;
    this.rotationPivot = this.pivot(e.clientX, e.clientY); // TrySetEntryRotationPivot on press
    this.host.setDragging(true);
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') { this.onTouchChange(e, 'move'); return; }
    if (this.button < 0) { this.host.hover(e.clientX, e.clientY); return; }
    const dx = e.clientX - this.lastX, dy = e.clientY - this.lastY;
    this.lastX = e.clientX; this.lastY = e.clientY;
    if (Math.hypot(e.clientX - this.downX, e.clientY - this.downY) >= CLICK_THRESHOLD_PX) this.moved = true;
    if (this.button === 0) {
      const r = rotationFromDrag(dx, dy);
      if (r) rotateAround(this.host.transform, this.rotationPivot ?? this.host.transform.position, r.axis, r.angleDeg);
    } else if (this.button === 2) {
      pan(this.host.transform, dx, dy);
    }
    this.host.changed();
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') { this.onTouchChange(e, 'up'); return; }
    const wasButton = this.button;
    this.button = -1;
    this.rotationPivot = null;
    this.host.setDragging(false);
    if (wasButton === 0 && !this.moved) this.host.click(e.clientX, e.clientY, { shiftKey: e.shiftKey, ctrlKey: e.ctrlKey || e.metaKey });
  };

  private onPointerLeave = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return;
    if (this.button < 0) this.host.hoverEnd();
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const scroll = wheelToScroll(e.deltaY, e.deltaMode, this.host.wheelDeltaToScroll ?? WHEEL_DELTA_TO_SCROLL) * ZOOM_SPEED;
    if (scroll === 0) return;
    const now = performance.now() / 1000;
    if (!this.zoomPivot || now - this.lastZoomTime > TRACKBALL.zoomPivotHoldSeconds) this.zoomPivot = this.pivot(e.clientX, e.clientY);
    this.lastZoomTime = now;
    zoomAbout(this.host.transform, this.zoomPivot, scroll);
    this.host.changed();
  };

  private onTouchChange(e: PointerEvent, phase: 'down' | 'move' | 'up'): void {
    const prevCount = this.touches.size;
    if (phase === 'up') this.touches.delete(e.pointerId);
    else this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const count = this.touches.size;
    const countChanged = count !== prevCount;
    const pts = [...this.touches.values()];
    if (count === 1) {
      const p = pts[0]!;
      if (countChanged) {
        this.lastX = p.x; this.lastY = p.y;
        this.rotationPivot = this.pivot(p.x, p.y);
        this.host.setDragging(true);
      } else if (phase === 'move') {
        const r = rotationFromDrag(p.x - this.lastX, p.y - this.lastY);
        if (r) { rotateAround(this.host.transform, this.rotationPivot ?? this.host.transform.position, r.axis, r.angleDeg); this.host.changed(); }
        this.lastX = p.x; this.lastY = p.y;
      }
    } else if (count >= 2) {
      const p0 = pts[0]!, p1 = pts[1]!;
      const dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
      const mid = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
      if (countChanged) {
        this.lastPinchDistance = dist; this.lastPinchMid = mid;
        this.twoFinger = { gesture: 'none', pinchAcc: 0, panAcc: 0 };
      } else if (phase === 'move') {
        const pinchDelta = dist - this.lastPinchDistance;
        const panDx = mid.x - this.lastPinchMid.x, panDy = mid.y - this.lastPinchMid.y;
        const g = updateTwoFingerGesture(this.twoFinger, pinchDelta, Math.hypot(panDx, panDy));
        if (g === 'zoom' && pinchDelta !== 0) zoomAbout(this.host.transform, this.host.transform.position, pinchDelta * ZOOM_SPEED * TRACKBALL.touchPinchFactor);
        else if (g === 'pan' && (panDx !== 0 || panDy !== 0)) pan(this.host.transform, panDx, panDy);
        this.lastPinchDistance = dist; this.lastPinchMid = mid;
        this.host.changed();
      }
      this.rotationPivot = null;
    } else {
      this.rotationPivot = null;
      this.host.setDragging(false);
    }
  }
}
